/* eslint-disable import/prefer-default-export */
import { useEffect, RefObject } from 'react'

type UseIntersectionObserverOptions = {
  /** Visibility threshold (0-1). Default 0.8 (80% visible) */
  threshold?: number
  /** Root margin for observer. Default '0px' */
  rootMargin?: string
  /** Fire callback only once, then unobserve. Default true */
  triggerOnce?: boolean
  /** Enable/disable observation. When toggled true, observer restarts. Default true */
  enabled?: boolean
}

/**
 * Hook for detecting when an element enters the viewport.
 * Accepts an external ref to observe, allowing you to reuse existing refs.
 *
 * @param ref - Ref to the element to observe
 * @param onVisible - Callback fired when element becomes visible
 * @param options - IntersectionObserver configuration options
 *
 * @example
 * const containerRef = useRef<HTMLDivElement>(null)
 * useIntersectionObserver(containerRef, () => {
 *   trackEvent('element_viewed')
 * })
 * return <div ref={containerRef}>...</div>
 */
export const useIntersectionObserver = <T extends HTMLElement>(
  ref: RefObject<T>,
  onVisible: () => void,
  options?: UseIntersectionObserverOptions,
): void => {
  const {
    threshold = 0.8,
    rootMargin = '0px',
    triggerOnce = true,
    enabled = true,
  } = options || {}

  useEffect(() => {
    if (!enabled) return undefined

    const element = ref.current
    if (!element) return undefined

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onVisible()
          if (triggerOnce) {
            observer.unobserve(element)
          }
        }
      },
      {
        root: null,
        rootMargin,
        threshold,
      },
    )

    observer.observe(element)

    return () => {
      observer.unobserve(element)
    }
  }, [ref, onVisible, threshold, rootMargin, triggerOnce, enabled])
}
